import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import MessageIcon from '@mui/icons-material/Message'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { formatDistanceToNow } from 'date-fns'
import { useSelector } from '../../../store/store'
import axiosInstance from '../../../utils/axiosInstance'

// Types
interface Comment {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  user: {
    id: string
    firstName: string
    lastName: string
  }
}

interface LeadCommentsProps {
  leadId: string
}

// Styled components
const CommentsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
}))

const CommentItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[100]}`,
  '&:last-child': {
    marginBottom: 0,
  },
}))

const CommentContent = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  lineHeight: 1.5,
}))

const CommentMeta = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}))

const NewCommentContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
}))

const LeadComments: React.FC<LeadCommentsProps> = ({ leadId }) => {
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [comments, setComments] = useState<Comment[]>([])
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const { userInfo } = useSelector((state) => state.user)

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!leadId) return

    try {
      setLoading(true)
      setError(null)
      const response = await axiosInstance.get(`/lead/${leadId}/comments`)
      setComments(response.data.comments || [])
    } catch (err: any) {
      console.error('Error fetching comments:', err)
      setError(err.response?.data?.message || 'Failed to fetch comments')
    } finally {
      setLoading(false)
    }
  }, [leadId])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !userInfo.id) return

    setIsSubmitting(true)
    setCreateError(null)

    try {
      const response = await axiosInstance.post(`/lead/${leadId}/comments`, {
        content: newComment.trim(),
      })

      // Add the new comment to the list
      if (response.data.comment) {
        setComments((prev) => [...prev, response.data.comment])
      }

      setNewComment('')
    } catch (error: any) {
      console.error('Error creating comment:', error)
      setCreateError(error.response?.data?.message || 'Failed to create comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault()
      handleSubmitComment()
    }
  }

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id)
    setEditingContent(comment.content)
    setCreateError(null)
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setEditingContent('')
    setCreateError(null)
  }

  const handleSaveEdit = async () => {
    if (!editingCommentId || !editingContent.trim()) return

    setIsUpdating(true)
    setCreateError(null)

    try {
      const response = await axiosInstance.put(`/lead/${leadId}/comments/${editingCommentId}`, {
        content: editingContent.trim(),
      })

      // Update the comment in the list
      if (response.data.comment) {
        setComments((prev) =>
          prev.map((comment) => (comment.id === editingCommentId ? response.data.comment : comment))
        )
      }

      setEditingCommentId(null)
      setEditingContent('')
    } catch (error: any) {
      console.error('Error updating comment:', error)
      setCreateError(error.response?.data?.message || 'Failed to update comment')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleEditKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault()
      handleSaveEdit()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      handleCancelEdit()
    }
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName?.charAt(0) || ''}`.toUpperCase()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <CommentsContainer>
      <Box display="flex" alignItems="center" mb={2}>
        <MessageIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" component="h3">
          Comments ({comments.length})
        </Typography>
      </Box>

      {/* Existing Comments */}
      {comments.length > 0 ? (
        <Stack spacing={0} mb={2}>
          {comments.map((comment) => (
            <CommentItem key={comment.id} elevation={0}>
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                  {getInitials(comment.user.firstName, comment.user.lastName)}
                </Avatar>
                <Box flex={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" component="span" fontWeight={600}>
                        {comment.user.firstName} {comment.user.lastName}
                      </Typography>
                      <CommentMeta>
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                        {comment.updatedAt !== comment.createdAt && ' (edited)'}
                      </CommentMeta>
                    </Box>
                    {/* Edit button - only show for user's own comments */}
                    {comment.user.id === userInfo.id && editingCommentId !== comment.id && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditComment(comment)}
                        sx={{ ml: 1, opacity: 0.7, '&:hover': { opacity: 1 } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* Comment content or edit form */}
                  {editingCommentId === comment.id ? (
                    <Box>
                      <TextField
                        multiline
                        rows={2}
                        fullWidth
                        value={editingContent}
                        onChange={(e) => setEditingContent(e.target.value)}
                        onKeyDown={handleEditKeyPress}
                        variant="outlined"
                        size="small"
                        disabled={isUpdating}
                        placeholder="Edit your comment... (Ctrl+Enter to save, Esc to cancel)"
                        sx={{
                          mb: 1,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                          },
                        }}
                      />
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={isUpdating ? <CircularProgress size={14} /> : <SaveIcon />}
                          onClick={handleSaveEdit}
                          disabled={!editingContent.trim() || isUpdating}
                          sx={{ minWidth: 80 }}
                        >
                          {isUpdating ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <CommentContent variant="body2">{comment.content}</CommentContent>
                  )}
                </Box>
              </Box>
            </CommentItem>
          ))}
        </Stack>
      ) : (
        <Box textAlign="center" py={3}>
          <Typography variant="body2" color="text.secondary">
            No comments yet. Be the first to add one!
          </Typography>
        </Box>
      )}

      {/* Add New Comment */}
      <NewCommentContainer>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
            {getInitials(userInfo.firstName, userInfo.lastName)}
          </Avatar>
          <Box flex={1}>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Add a comment... (Ctrl+Enter to submit)"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              size="small"
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <Typography variant="caption" color="text.secondary">
                Ctrl+Enter to submit • Edit your comments anytime
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || isSubmitting}
                sx={{ minWidth: 100 }}
              >
                {isSubmitting ? 'Posting...' : 'Comment'}
              </Button>
            </Box>
          </Box>
        </Box>
      </NewCommentContainer>

      {createError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {createError}
        </Alert>
      )}
    </CommentsContainer>
  )
}

export default LeadComments
